import { Sparkles, TrendingUp } from "lucide-react";
import type { UpsellFeatureItem } from "@/shared/types/common";
import { getLucideIcon } from "@/shared/utils/lucideIcon.utils";
import {
  CARD_BACKGROUNDS,
  COLOR_CHARCOAL,
  COLOR_MUTED,
} from "../constants/upsellPage.constants";
import MediaCard from "./primitives/MediaCard";
import Pill from "./primitives/Pill";

type BenefitsSectionProps = {
  title: string
  intro?: string
  benefits: UpsellFeatureItem[]
};

const BenefitsSection = ({ title, intro, benefits }: BenefitsSectionProps) => {
  if (benefits.length === 0) return null;

  return (
    <section className="mx-auto w-full max-w-5xl px-4 py-20 sm:py-24">
      <div className="mb-16 flex flex-col items-center text-center">
        <Pill
          variant="tinted-purple"
          size="sm"
          icon={<TrendingUp size={14} strokeWidth={2} />}
          className="animate-upsell-fade-in mb-5"
        >
          Les bénéfices
        </Pill>
        <h2
          className="animate-upsell-fade-in-up font-light tracking-tight"
          style={{
            color: COLOR_CHARCOAL,
            fontSize: "clamp(2rem, 4vw, 3rem)",
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
            animationDelay: "0.05s",
          }}
        >
          {title}
        </h2>
        {intro && (
          <p
            className="animate-upsell-fade-in-up mt-4 max-w-xl text-base sm:text-lg"
            style={{
              color: COLOR_MUTED,
              lineHeight: 1.55,
              animationDelay: "0.1s",
            }}
          >
            {intro}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        {benefits.map((benefit, index) => {
          const BenefitIcon = getLucideIcon(benefit.icon) ?? Sparkles;
          return (
            <MediaCard
              key={benefit.text}
              backgroundUrl={CARD_BACKGROUNDS[index % CARD_BACKGROUNDS.length]}
              revealDelay={index}
              media={(
                <BenefitIcon
                  size={32}
                  strokeWidth={1.75}
                  style={{ color: COLOR_CHARCOAL }}
                  aria-hidden="true"
                />
              )}
              body={(
                <p
                  className="font-medium leading-snug"
                  style={{
                    color: COLOR_CHARCOAL,
                    fontSize: "16px",
                    maxWidth: "20ch",
                  }}
                >
                  {benefit.text}
                </p>
              )}
            />
          );
        })}
      </div>
    </section>
  );
};

export default BenefitsSection;
